import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import API_CONFIG from '../../config/api.config';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import companySettingsService from '../../services/companySettingsService';
import { getAuthHeader, updateCompanyInfo } from '../../services/authService';

const CompanySettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: '',
    gst: '',
    addressLine1: '',
    addressLine2: '',
    contact: '',
    email: '',
    websitePath: '',
    orderPrefix: '',
    orderResetFrequency: 'MONTHLY',
    skuPrefix: '',
    skuPattern: '001',
    logoUrl: '',
    dayCloseEnabled: false,
    enableBatchWise: true
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [suggestedPath, setSuggestedPath] = useState('');
  const [websitePathError, setWebsitePathError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactError, setContactError] = useState('');
  const [planFeatures, setPlanFeatures] = useState(null);

  // Helper function to get base URL (without /api)
  const getBaseUrl = () => {
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    // If it's localhost or IP, return as is, otherwise use window.location for production
    if (baseUrl.includes('localhost') || baseUrl.match(/^\d+\.\d+\.\d+\.\d+/)) {
      return baseUrl;
    }
    // For production, use current window location
    return window.location.origin;
  };

  // Helper function to get full logo URL
  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return '';
    // If logoUrl already starts with /api, construct full URL
    if (logoUrl.startsWith('/api')) {
      const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
      return `${baseUrl}${logoUrl}`;
    }
    // If it's already a full URL, return as is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    // Otherwise, assume it's a relative path and construct URL
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    return `${baseUrl}/api${logoUrl.startsWith('/') ? logoUrl : '/' + logoUrl}`;
  };

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Reactively track current subscription plan features (for website domain visibility)
  useEffect(() => {
    // Read any features that Navigation has already loaded
    if (typeof window !== 'undefined' && window.__PLAN_FEATURES__) {
      setPlanFeatures(window.__PLAN_FEATURES__);
    }

    const handlePlanFeaturesUpdated = (event) => {
      const nextFeatures =
        event && event.detail
          ? event.detail
          : (typeof window !== 'undefined' && window.__PLAN_FEATURES__) || null;
      setPlanFeatures(nextFeatures);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
      }
    };
  }, []);

  // Check for changes
  useEffect(() => {
    const changed = Object.keys(settings).some(key =>
      settings[key] !== originalSettings[key]
    );
    setHasChanges(changed);
  }, [settings, originalSettings]);

  // Auto-suggest website path when company name changes (only if websitePath is empty)
  useEffect(() => {
    const generateSuggestedPath = async () => {
      if (settings.name && !settings.websitePath) {
        try {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/companies/settings/website-path/suggest?companyName=${encodeURIComponent(settings.name)}`,
            { headers: getAuthHeader() }
          );
          if (response.ok) {
            const data = await response.json();
            setSuggestedPath(data.suggestedPath || '');
          }
        } catch (error) {
          console.error('Error generating suggested path:', error);
          // Fallback: generate simple suggestion client-side
          const simple = settings.name.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '') || 'mywebsite';
          setSuggestedPath(simple);
        }
      } else if (!settings.name) {
        setSuggestedPath('');
      }
    };

    generateSuggestedPath();
  }, [settings.name, settings.websitePath]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await companySettingsService.getSettings();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      enqueueSnackbar('Failed to load company settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    if (field === 'contact') {
      value = value.replace(/\D/g, '').slice(0, 15);
    }
    if (field === 'email') {
      // no transform, only validate
    }
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear website path error when user types
    if (field === 'websitePath') {
      setWebsitePathError('');
    }
    // Basic immediate validations
    if (field === 'email') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      setEmailError(value && !pattern.test(value) ? 'Invalid email format' : '');
    }
    if (field === 'contact') {
      setContactError(value && value.replace(/\D/g, '').length === 0 ? 'Phone must contain digits' : '');
    }
  };

  const handleToggleChange = (field) => (event) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Validate email/phone
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (settings.email && !pattern.test(settings.email)) {
        setEmailError('Invalid email format');
        setSaving(false);
        return;
      }
      if (settings.contact) {
        const digits = settings.contact.replace(/\D/g, '');
        if (!digits || digits.length > 15) {
          setContactError(!digits ? 'Phone must contain digits' : 'Phone must be at most 15 digits');
          setSaving(false);
          return;
        }
      }

      // Validate website path if provided
      if (settings.websitePath && settings.websitePath.trim()) {
        const cleanPath = settings.websitePath.trim().toLowerCase();
        try {
          const checkResponse = await fetch(
            `${API_CONFIG.BASE_URL}/companies/settings/website-path/check?websitePath=${encodeURIComponent(cleanPath)}`,
            { headers: getAuthHeader() }
          );
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            if (!checkData.available) {
              setWebsitePathError('This domain is already taken. Please choose a different one.');
              setSaving(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking website path:', error);
          // Continue with save even if check fails (backend will validate)
        }
      }

      const response = await companySettingsService.updateSettings(settings);

      if (response.success) {
        setOriginalSettings(settings);

        // Sync with localStorage for top bar display
        updateCompanyInfo({ name: settings.name, logoUrl: settings.logoUrl });

        enqueueSnackbar(response.message, { variant: 'success' });

        if (response.note) {
          enqueueSnackbar(response.note, {
            variant: 'info',
            autoHideDuration: 8000
          });
        }
      } else {
        enqueueSnackbar(response.message || 'Failed to update settings', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to save company settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please select an image file', { variant: 'error' });
        return;
      }

      // Validate file size (3MB limit)
      if (file.size > 3 * 1024 * 1024) {
        enqueueSnackbar('File size must be less than 3MB', { variant: 'error' });
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      enqueueSnackbar('Please select a file to upload', { variant: 'error' });
      return;
    }

    try {
      setUploadingLogo(true);
      const response = await companySettingsService.uploadLogo(logoFile);

      if (response.success) {
        setSettings(prev => ({
          ...prev,
          logoUrl: response.logoUrl
        }));

        // Update localStorage for top bar
        updateCompanyInfo({ logoUrl: response.logoUrl });

        setLogoFile(null);
        setLogoPreview(null);
        enqueueSnackbar(response.message, { variant: 'success' });
      } else {
        enqueueSnackbar(response.message || 'Failed to upload logo', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to upload logo', { variant: 'error' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({
      ...prev,
      logoUrl: ''
    }));

    // Update localStorage for top bar
    updateCompanyInfo({ logoUrl: '' });

    setLogoFile(null);
    setLogoPreview(null);
  };

  // Website domain should only be visible when ecommerce website feature is enabled.
  // For legacy plans or while loading flags, we keep the legacy behaviour (visible).
  // Normalize value: only boolean true or string "true" counts as enabled (API/DB may return string "false").
  const ecommerceEnabled = planFeatures && (
    planFeatures.ecommerce_website === true ||
    (typeof planFeatures.ecommerce_website === 'string' && planFeatures.ecommerce_website.toLowerCase() === 'true')
  );
  const shouldShowWebsiteDomain =
    !planFeatures ||
    planFeatures._treatAllEnabled ||
    !!ecommerceEnabled;

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            underline="hover"
          >
            Dashboard
          </Link>
          <Typography color="text.primary">Company Settings</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Company Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your company information that appears on invoices and receipts
          </Typography>
        </Box>

        {/* Settings Form */}
        <Paper elevation={2}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Company Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={settings.name || ''}
                  onChange={handleInputChange('name')}
                  variant="outlined"
                  helperText="This will appear on all invoices and receipts"
                />
              </Grid>

              {/* GST Number */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  value={settings.gst || ''}
                  onChange={handleInputChange('gst')}
                  variant="outlined"
                  helperText="GST Number for your company"
                />
              </Grid>

              {/* Address Line 1 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={settings.addressLine1 || ''}
                  onChange={handleInputChange('addressLine1')}
                  variant="outlined"
                  helperText="Street address, building name, unit number"
                />
              </Grid>

              {/* Address Line 2 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={settings.addressLine2 || ''}
                  onChange={handleInputChange('addressLine2')}
                  variant="outlined"
                  helperText="City, postal code, country"
                />
              </Grid>

              {/* Contact */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  value={settings.contact || ''}
                  onChange={handleInputChange('contact')}
                  variant="outlined"
                  error={!!contactError}
                  helperText="Phone number with country code"
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={settings.email || ''}
                  onChange={handleInputChange('email')}
                  variant="outlined"
                  error={!!emailError}
                  helperText="Company contact email"
                />
              </Grid>

              {/* Website Path / Domain – gated by ecommerce feature; legacy plans see it as before */}
              {shouldShowWebsiteDomain && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website Domain"
                    value={settings.websitePath || ''}
                    onChange={handleInputChange('websitePath')}
                    variant="outlined"
                    error={!!websitePathError}
                    placeholder={suggestedPath || 'mywebsite'}
                    helperText={
                      websitePathError ||
                      (settings.websitePath
                        ? `Your ecommerce site will be accessible at: ${getBaseUrl()}/${settings.websitePath || suggestedPath || 'mywebsite'}`
                        : suggestedPath
                          ? `Suggested: ${suggestedPath}. Your site will be at: ${getBaseUrl()}/${suggestedPath}`
                          : `Enter your website path (e.g., mywebsite). Your ecommerce site will be accessible at ${getBaseUrl()}/your-path`)
                    }
                    inputProps={{
                      pattern: '[a-z0-9-]+',
                      style: { textTransform: 'lowercase' }
                    }}
                    onInput={(e) => {
                      // Convert to lowercase and remove spaces
                      e.target.value = e.target.value.toLowerCase().replace(/\s+/g, '');
                    }}
                    onFocus={() => {
                      // Auto-fill suggested path if field is empty
                      if (!settings.websitePath && suggestedPath) {
                        setSettings(prev => ({
                          ...prev,
                          websitePath: suggestedPath
                        }));
                      }
                    }}
                  />
                </Grid>
              )}

              {/* Company Logo */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  Company Logo
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {/* Logo Preview */}
                  <Avatar
                    src={logoPreview || getLogoUrl(settings.logoUrl) || ''}
                    sx={{ width: 80, height: 80 }}
                    variant="rounded"
                  >
                    <BusinessIcon sx={{ fontSize: 40 }} />
                  </Avatar>

                  {/* Upload Controls */}
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={handleLogoChange}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingLogo}
                      >
                        {logoFile ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                    </label>

                    {logoFile && (
                      <Button
                        variant="contained"
                        onClick={handleLogoUpload}
                        disabled={uploadingLogo}
                        sx={{ ml: 1 }}
                        startIcon={uploadingLogo ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload'}
                      </Button>
                    )}

                    {(settings.logoUrl || logoFile) && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveLogo}
                        startIcon={<DeleteIcon />}
                        sx={{ ml: 1 }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  Upload your company logo (PNG, JPG, JPEG, GIF). Maximum file size: 3MB.
                  The logo will appear on invoices and receipts.
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Order Numbering Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
              Order Numbering Settings
            </Typography>
            <Grid container spacing={3}>
              {/* Order Prefix */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Order Prefix"
                  value={settings.orderPrefix || ''}
                  onChange={handleInputChange('orderPrefix')}
                  variant="outlined"
                  helperText="Prefix for order numbers (e.g., EMI123, ABC, etc.)"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* Order Reset Frequency */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Order Reset Frequency</InputLabel>
                  <Select
                    value={settings.orderResetFrequency || 'MONTHLY'}
                    onChange={handleInputChange('orderResetFrequency')}
                    label="Order Reset Frequency"
                  >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="YEARLY">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* SKU Auto-generation Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              SKU Auto-generation Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When creating new products, SKU will be auto-generated based on these settings. Leave empty to use legacy auto-generation.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SKU Prefix"
                  value={settings.skuPrefix || ''}
                  onChange={handleInputChange('skuPrefix')}
                  variant="outlined"
                  helperText="Prefix for product SKUs (e.g., GTR, TJR). All new products will start with this prefix."
                  inputProps={{ maxLength: 20 }}
                  placeholder="e.g., GTR"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SKU Pattern"
                  value={settings.skuPattern || '001'}
                  onChange={handleInputChange('skuPattern')}
                  variant="outlined"
                  helperText="Number format: 001=3 digits (GTR001), 0001=4 digits (GTR0001), 01=2 digits (GTR01)"
                  inputProps={{ maxLength: 10 }}
                  placeholder="e.g., 001"
                />
              </Grid>
            </Grid>
            {(settings.skuPrefix || '').trim() && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Preview:</strong> Next product SKUs will be: {settings.skuPrefix}001, {settings.skuPrefix}002, {settings.skuPrefix}003...
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Day Close Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
              Day Close Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.dayCloseEnabled || false}
                      onChange={handleToggleChange('dayCloseEnabled')}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Enable Day Begin/Day Close
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        When enabled, users must begin the day before creating bills. When disabled, billing works without day begin requirement.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Batch-wise / Expiry Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
              Batch Wise / Expiry Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableBatchWise || false}
                      onChange={handleToggleChange('enableBatchWise')}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Enable Batch Wise Feature
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        When enabled, Product creation and POS will show Batch No / MFD / Expiry fields and selection.
                        When disabled, normal product flow is used.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>

            {/* Info Alert */}
            {hasChanges && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Changes will be applied to new invoices immediately.
                  For permanent storage, the application configuration will need to be updated.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Paper>

        {/* Preview Section */}
        <Paper elevation={2} sx={{ mt: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This is how your company information will appear on invoices:
            </Typography>

            <Box
              sx={{
                p: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                textAlign: 'right'
              }}
            >
              {/* Logo and Company Name */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar
                  src={getLogoUrl(settings.logoUrl) || ''}
                  sx={{ width: 60, height: 60 }}
                  variant="rounded"
                >
                  <BusinessIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {settings.name || 'COMPANY NAME'}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                {settings.addressLine1 || 'ADDRESS LINE 1'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                {settings.addressLine2 || 'ADDRESS LINE 2'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                Contact - {settings.contact || 'CONTACT NUMBER'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                Email- {settings.email || 'EMAIL@COMPANY.COM'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                GST-{settings.gst || 'GST_NUMBER'}
              </Typography>
            </Box>
          </CardContent>
        </Paper>

        {/* Order Numbering Preview Section */}
        <Paper elevation={2} sx={{ mt: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Order Numbering Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This is how your order numbers will be generated:
            </Typography>

            <Box
              sx={{
                p: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}
            >
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Prefix:</strong> {settings.orderPrefix || 'EMI123'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Reset Frequency:</strong> {settings.orderResetFrequency === 'DAILY' ? 'Daily' :
                  settings.orderResetFrequency === 'MONTHLY' ? 'Monthly' : 'Yearly'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Example Order Numbers:</strong>
              </Typography>
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {settings.orderPrefix || 'EMI123'}-001
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {settings.orderPrefix || 'EMI123'}-002
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {settings.orderPrefix || 'EMI123'}-003
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                * Numbers reset based on the selected frequency (Daily/Monthly/Yearly)
              </Typography>
            </Box>
          </CardContent>
        </Paper>
      </Container>
    </Layout>
  );
};

export default CompanySettings; 
